import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { authAPI } from '../services/api';

function VerifyEmail() {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(token && token !== 'resend' ? 'verifying' : 'resend'); // verifying, success, error, resend
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState(location.state?.email || '');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (token && token !== 'resend') {
      verifyEmailToken();
    }
  }, [token]);

  const verifyEmailToken = async () => {
    try {
      const response = await authAPI.verifyEmail(token);
      setStatus('success');
      setMessage(response.data.message);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.message || 'Email verification failed. The link may be invalid or expired.');
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    setResendMessage({ type: '', text: '' });
    setResendLoading(true);

    try {
      const response = await authAPI.resendVerification({ email: resendEmail });
      setResendMessage({ 
        type: 'success', 
        text: response.data.message 
      });
      setResendEmail('');
    } catch (error) {
      setResendMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to resend verification email' 
      });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '40px',
        maxWidth: '500px',
        width: '100%',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
      }}>
        {status === 'verifying' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              margin: '0 auto 20px',
              animation: 'spin 1s linear infinite'
            }}></div>
            <h2 style={{ color: '#1a202c', marginBottom: '10px' }}>Verifying Email...</h2>
            <p style={{ color: '#718096' }}>Please wait while we verify your email address.</p>
          </div>
        )}

        {status === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: '#10b981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '40px',
              color: 'white'
            }}>✓</div>
            <h2 style={{ color: '#10b981', marginBottom: '10px' }}>Email Verified!</h2>
            <p style={{ color: '#718096', marginBottom: '20px' }}>{message}</p>
            <p style={{ color: '#718096', fontSize: '14px' }}>
              Redirecting to login page in 3 seconds...
            </p>
            <Link 
              to="/login" 
              style={{
                display: 'inline-block',
                marginTop: '10px',
                color: '#667eea',
                textDecoration: 'none',
                fontWeight: 600
              }}
            >
              Click here if not redirected
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: '#ef4444',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: '40px',
                color: 'white'
              }}>✕</div>
              <h2 style={{ color: '#ef4444', marginBottom: '10px' }}>Verification Failed</h2>
              <p style={{ color: '#718096', marginBottom: '20px' }}>{message}</p>
            </div>

            <div style={{
              borderTop: '1px solid #e5e7eb',
              paddingTop: '30px'
            }}>
              <h3 style={{ 
                fontSize: '18px', 
                marginBottom: '15px',
                color: '#1a202c'
              }}>
                Request New Verification Link
              </h3>

              {resendMessage.text && (
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '15px',
                  background: resendMessage.type === 'success' ? '#f0fdf4' : '#fef2f2',
                  border: `1px solid ${resendMessage.type === 'success' ? '#86efac' : '#fca5a5'}`,
                  color: resendMessage.type === 'success' ? '#166534' : '#991b1b',
                  fontSize: '14px'
                }}>
                  {resendMessage.text}
                </div>
              )}

              <form onSubmit={handleResendVerification}>
                <div className="form-group" style={{ marginBottom: '15px' }}>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    color: '#374151',
                    fontWeight: 500
                  }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    required
                    placeholder="Enter your email"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={resendLoading}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: resendLoading ? '#9ca3af' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: resendLoading ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s'
                  }}
                >
                  {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                </button>
              </form>

              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <Link 
                  to="/login" 
                  style={{
                    color: '#667eea',
                    textDecoration: 'none',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  ← Back to Login
                </Link>
              </div>
            </div>
          </div>
        )}

        {status === 'resend' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: '#667eea',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: '40px',
                color: 'white'
              }}>✉️</div>
              <h2 style={{ color: '#667eea', marginBottom: '10px' }}>Resend Verification Email</h2>
              <p style={{ color: '#718096', marginBottom: '20px' }}>
                Enter your email address to receive a new verification link
              </p>
            </div>

            {resendMessage.text && (
              <div style={{
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '15px',
                background: resendMessage.type === 'success' ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${resendMessage.type === 'success' ? '#86efac' : '#fca5a5'}`,
                color: resendMessage.type === 'success' ? '#166534' : '#991b1b',
                fontSize: '14px'
              }}>
                {resendMessage.text}
              </div>
            )}

            <form onSubmit={handleResendVerification}>
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '8px',
                  color: '#374151',
                  fontWeight: 500
                }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={resendLoading}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: resendLoading ? '#9ca3af' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: resendLoading ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s'
                }}
              >
                {resendLoading ? 'Sending...' : 'Send Verification Email'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <Link 
                to="/login" 
                style={{
                  color: '#667eea',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                ← Back to Login
              </Link>
            </div>
          </div>
        )}

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}

export default VerifyEmail;
